import { createContext, useEffect, useState } from "react";

import * as mqtt from "mqtt";

import { useAppDispatch } from "../hooks/reduxHooks";
import { updateDevice } from "../store/device";
import { updateDeviceCommand } from "../store/deviceCommand";

const devaultMqttContext: MqttContext = {
	mqttConnected: false,
	mqttStatus: "",
	mqttStatusType: "info",
	mqttError: "",
	mqttPublish: () => {},
	mqttSubscribe: () => {},
};

export const MqttContext = createContext<MqttContext>(devaultMqttContext);

export const mqttStatusTypes: MqttStatusTypes = {
	Unknown: "info",
	Connected: "success",
	Disconnected: "error",
	Reconnecting: "info",
	Offline: "error",
	Closed: "error",
	Ended: "error",
};

interface MqttProviderProps {
	children: React.ReactNode;
	mqttBroker: string;
	mqttOptions: mqtt.IClientOptions;
	companyABBR: string;
}

function MqttProvider({ mqttBroker, mqttOptions, companyABBR, children }: MqttProviderProps) {
	const [mqttClient, setMqttClient] = useState<mqtt.MqttClient>();
	const [mqttClientInit, setMqttClientInit] = useState<boolean>(false);
	const [mqttConnected, setMqttConnected] = useState<boolean>(false);
	const [mqttError, setMqttError] = useState<string>("");
	const [mqttStatus, setMqttStatus] = useState<MqttStatusMessage>("Unknown");
	const [mqttStatusType, setMqttStatusType] = useState<MqttStatusType>("info");

	const dispatch = useAppDispatch();

	const mqttPublish = (topic: string, message: string | Buffer, opts: mqtt.IClientPublishOptions = { retain: false }) => {
		if (mqttClient) {
			//console.log("mqttPublish", companyUUID + topic, message);
			mqttClient.publish(companyABBR + topic, message, opts, (err) => {
				if (err) {
					console.log("mqttPublish Error", err);
				}
			});
		} else {
			setMqttError("mqttClient not set");
		}
	};

	const mqttSubscribe = (topic: string, opts: mqtt.IClientSubscribeOptions = { qos: 0 }) => {
		if (mqttClient) {
			console.log("mqttSubscribe", companyABBR + topic);
			mqttClient.subscribe(companyABBR + topic, opts);
		} else {
			setMqttError("mqttClient not set");
		}
	};

	const processMessage = (topic: string, message: Buffer) => {
		try {
			//console.log("mqttMessage", topic, message);
			let msg = JSON.parse(new TextDecoder("utf-8").decode(message));
			let tpc = topic;
			let id = Number(tpc.split("/")[2]);
			if (["count", "settings", "status"].includes(msg?.msgType)) {
				//console.log("mqttMessage", msg);
				dispatch(updateDevice({ id: id, device: msg }));
			}
			if (["setCount", "resetCount"].includes(msg?.cmdType)) {
				dispatch(updateDeviceCommand({ id: id, command: msg?.cmdType }));
			}
		} catch (err) {
			console.log("mqttMessage", err);
		}
	};
	useEffect(() => {
		console.log("MqttProvider useEffect - mqttBroker:", mqttBroker); // Debug log
		if (!mqttBroker || !mqttOptions || !companyABBR) {
			console.log("mqtt connect not called - missing required props");
			console.log("mqttBroker:", mqttBroker);
			console.log("companyABBR:", companyABBR);
			return;
		}

		// Clean up existing client
		if (mqttClient) {
			mqttClient.end();
			setMqttClientInit(false);
		}

		console.log("mqtt connect - connecting to:", mqttBroker);
		const client = mqtt.connect(mqttBroker, mqttOptions);
		setMqttClient(client);

		return () => {
			client.end();
			console.log("mqtt disconnected from:", mqttBroker);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mqttBroker, mqttOptions, companyABBR]);

	useEffect(() => {
		if (mqttClient && !mqttClientInit) {
			mqttClient.on("error", (err) => {
				console.log("MQTT error: ", err);
				setMqttError(err.message);
				mqttClient.end();
			});
			mqttClient.on("disconnect", () => {
				setMqttStatus(`Disconnected`);
			});
			mqttClient.on("connect", () => {
				setMqttStatus(`Connected`);
				setMqttConnected(true);
			});
			mqttClient.on("reconnect", () => {
				setMqttStatus(`Reconnecting`);
			});
			mqttClient.on("close", () => {
				setMqttStatus(`Closed`);
			});
			mqttClient.on("end", () => {
				console.log("mqttClient ended");
				setMqttStatus(`Ended`);
			});
			mqttClient.on("offline", () => {
				setMqttStatus(`Offline`);
			});
			mqttClient.on("message", (topic, message, packet) => {
				console.log("mqttMessage", topic, message, mqttStatus);
				if (mqttStatus !== "Connected") {
					console.log("Received message while status is Ended, updating to Connected");
					setMqttStatus("Connected");
					setMqttConnected(true);
				}
				processMessage(topic, message);
			});
			let t = "/countc/#";
			console.log("subscribing to " + t);
			mqttSubscribe(t, { qos: 0 });
			setMqttClientInit(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mqttClient]);

	useEffect(() => {
		setMqttStatusType(mqttStatusTypes[mqttStatus || "Unknown"]);
	}, [mqttStatus]);

	const contextValue: MqttContext = {
		mqttConnected: mqttConnected,
		mqttStatus: mqttStatus,
		mqttError: mqttError,
		mqttStatusType: mqttStatusType,
		mqttPublish: mqttPublish,
		mqttSubscribe: mqttSubscribe,
	};

	//console.log("Context value: ", contextValue);
	return <MqttContext.Provider value={contextValue}>{children}</MqttContext.Provider>;
}

export default MqttProvider;
